import React, {useMemo, ReactNode} from "react";
import {gql, useQuery} from "@apollo/client";
import {useParams} from "react-router-dom";
import {View} from "@unibuddy/patron";

import {
    GetChatConversationFromLegacyIdQueryQuery,
    GetChatConversationFromLegacyIdQueryQueryVariables,
    GetChatConversationIdQueryQuery,
    GetChatConversationIdQueryQueryVariables,
} from "../../../../types/generated";
import LoadingChat from "../components/LoadingChat/LoadingChat";
import {pageNames} from "../../../../shared/AnalyticsNew/constants/productSpecification";
import {DownloadUnibuddyAppBanner} from "../../DownloadUnibuddyApp/DownloadUnibuddyAppBanner";

export const GetChatConversationFromIdQuery = gql`
    query getChatConversationIdQuery($id: String!) {
        getChatConversation(id: $id) {
            id
            sourceId
        }
    }
`;

export const GetChatConversationFromLegacyIdQuery = gql`
    query getChatConversationFromLegacyIdQuery($id: String!) {
        getChatConversationFromLegacyId(id: $id) {
            id
            sourceId
        }
    }
`;

type ChatContextType = {
    chatGroupId: string;
    conversationId: string;
};

const ChatContext = React.createContext<ChatContextType>({
    chatGroupId: "",
    conversationId: "",
});

type ChatProviderProps = {
    children: ReactNode;
};

export const ChatProvider = ({children}: ChatProviderProps): JSX.Element | null => {
    const {chatGroupId, conversationId} = useParams<{
        chatGroupId: string | undefined;
        conversationId: string | undefined;
    }>();

    const {
        data: chatData,
        loading: chatLoading,
        error: chatError,
    } = useQuery<GetChatConversationIdQueryQuery, GetChatConversationIdQueryQueryVariables>(
        GetChatConversationFromIdQuery,
        {
            variables: {
                id: conversationId,
            },
            skip: !conversationId,
        },
    );

    const {
        data: legacyChatData,
        loading: legacyChatLoading,
        error: legacyChatError,
    } = useQuery<
        GetChatConversationFromLegacyIdQueryQuery,
        GetChatConversationFromLegacyIdQueryQueryVariables
    >(GetChatConversationFromLegacyIdQuery, {
        variables: {
            id: chatGroupId,
        },
        skip: !chatGroupId,
    });

    const value: {chatGroupId: string; conversationId: string} = useMemo(() => {
        return {
            chatGroupId: chatGroupId ?? chatData?.getChatConversation?.sourceId ?? "",
            conversationId:
                conversationId ?? legacyChatData?.getChatConversationFromLegacyId?.id ?? "",
        };
    }, [chatData, legacyChatData, chatGroupId, conversationId]);

    if (!chatGroupId && !conversationId) {
        return <>{children}</>;
    }

    if (chatLoading || legacyChatLoading) {
        return (
            <>
                <DownloadUnibuddyAppBanner page={pageNames.chat} />
                <View h="550px">
                    <LoadingChat />
                </View>
            </>
        );
    }

    if (
        (!legacyChatData?.getChatConversationFromLegacyId && chatGroupId) ||
        (!chatData?.getChatConversation && conversationId) ||
        legacyChatError ||
        chatError
    ) {
        return <p>An error occurred. Please try again later.</p>;
    }

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => React.useContext(ChatContext);
